/**
 * Team Configuration Provider
 * React context for accessing team configuration throughout the app
 * Adapted from V2 for React 18 CRA
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getTeamConfig,
  defaultConfig,
  defaultTeamId,
  teamExists,
  teamRegistry,
  SPORT_CODES,
  SPORT_CATEGORIES,
  getSportCodeForTeam,
  getSportCategory,
} from './teams';
import {
  normalizeSelectedTeamSelection,
} from '../chat/sportContext';
import { hexToRgb } from '../utils';
import config from '../../utils/config';

// Environment variable for team selection (CRA style)
const ENV_TEAM_ID = process.env.REACT_APP_TEAM_ID;

// API endpoint for team configs - use full backend URL to avoid relative path issues
const TEAM_CONFIG_API = `${config.API_BASE_URL}/team-configs`;

// Local storage keys
const STORAGE_KEY = 'slam_team_id';
const SPORT_STORAGE_KEY = 'slam_sport_selection';
const SPORT_SELECTED_FLAG = 'slam_sport_selected';
const SELECTED_TEAM_KEY = 'slam_selected_team';
const API_CONFIG_CACHE_KEY = 'slam_team_configs_cache';
const API_CONFIG_CACHE_EXPIRY = 'slam_team_configs_expiry';

// Cache duration: 5 minutes
const CACHE_DURATION_MS = 5 * 60 * 1000;

// Sport-to-default-team mapping (uses sportCode now)
const SPORT_DEFAULT_TEAMS = {
  baseball: 'slam',      // Generic baseball (CBB)
  basketball: 'slamball', // Basketball (MBB)
  golf: 'slamgolf',      // Golf (GOLF)
  soccer: 'slamsoccer',  // Soccer (SOC)
  football: null,        // No default yet
  other: null,           // No default yet
};

// Sport category to sportCode mapping for selectSport function
const SPORT_CATEGORY_TO_CODES = {
  baseball: ['MLB', 'CBB'],
  basketball: ['NBA', 'MBB', 'WBB'],
  football: ['NFL', 'CFB'],
  soccer: ['SOC'],
  hockey: ['HKY'],
  volleyball: ['VB'],
  golf: ['GOLF'],
  tennis: ['TEN'],
};

/**
 * Get initial team ID from environment or storage
 * Priority: localStorage (user choice) > ENV > default
 */
function getInitialTeamId() {
  // Check localStorage FIRST to respect user's sport selection
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && teamExists(stored)) {
      return stored;
    }
  }

  // Then check environment variable (build-time default)
  if (ENV_TEAM_ID && teamExists(ENV_TEAM_ID)) {
    return ENV_TEAM_ID;
  }

  return defaultTeamId;
}

/**
 * Fetch team configs from API with caching
 * Falls back to hardcoded configs on error
 * @returns {Promise<Object>} Merged team registry
 */
async function fetchTeamConfigsFromAPI() {
  // Check cache first
  if (typeof window !== 'undefined') {
    const cached = localStorage.getItem(API_CONFIG_CACHE_KEY);
    const expiry = localStorage.getItem(API_CONFIG_CACHE_EXPIRY);

    if (cached && expiry && Date.now() < parseInt(expiry, 10)) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        console.warn('Failed to parse cached team configs:', e);
      }
    }
  }

  try {
    const response = await fetch(TEAM_CONFIG_API);
    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    // API returns {success, configs: [...]} - extract the configs array
    const apiConfigs = Array.isArray(data) ? data : (data.configs || []);

    // Merge API configs with hardcoded (API takes precedence)
    const merged = { ...teamRegistry };
    for (const config of apiConfigs) {
      if (config.id) {
        merged[config.id] = config;
      }
    }

    // Cache the result
    if (typeof window !== 'undefined') {
      localStorage.setItem(API_CONFIG_CACHE_KEY, JSON.stringify(merged));
      localStorage.setItem(API_CONFIG_CACHE_EXPIRY, String(Date.now() + CACHE_DURATION_MS));
    }

    return merged;
  } catch (error) {
    console.warn('Failed to fetch team configs from API, using hardcoded fallback:', error);
    return teamRegistry;
  }
}

/**
 * Merge API config with hardcoded config (API overrides)
 * @param {Object} hardcoded - Hardcoded config
 * @param {Object} api - API config
 * @returns {Object} Merged config
 */
function mergeTeamConfig(hardcoded, api) {
  if (!api) return hardcoded;
  if (!hardcoded) return api;

  return {
    ...hardcoded,
    ...api,
    // Deep merge specific nested objects
    branding: { ...hardcoded.branding, ...api.branding },
    colors: {
      ...hardcoded.colors,
      ...api.colors,
      text: { ...hardcoded.colors?.text, ...api.colors?.text },
      status: { ...hardcoded.colors?.status, ...api.colors?.status },
    },
    logos: { ...hardcoded.logos, ...api.logos },
    analysis: {
      ...hardcoded.analysis,
      ...api.analysis,
      focusAreas: api.analysis?.focusAreas || hardcoded.analysis?.focusAreas,
      terminology: { ...hardcoded.analysis?.terminology, ...api.analysis?.terminology },
    },
    features: {
      ...hardcoded.features,
      ...api.features,
      exports: { ...hardcoded.features?.exports, ...api.features?.exports },
      custom: { ...hardcoded.features?.custom, ...api.features?.custom },
    },
  };
}

/**
 * Convert team colors to CSS custom properties
 * @param {Object} config - Team configuration
 * @returns {Object} CSS custom properties object
 */
function colorsToCSSVariables(config) {
  const { colors } = config;

  return {
    '--team-primary': colors.primary,
    '--team-primary-hover': colors.primaryHover,
    '--team-secondary': colors.secondary,
    '--team-accent': colors.accent,
    '--team-background': colors.background,
    '--team-surface': colors.surface,
    '--team-surface-hover': colors.surfaceHover,
    '--team-border': colors.border,
    '--team-text-primary': colors.text.primary,
    '--team-text-secondary': colors.text.secondary,
    '--team-text-on-primary': colors.text.onPrimary,
    '--team-success': colors.status.success,
    '--team-warning': colors.status.warning,
    '--team-error': colors.status.error,
    '--team-info': colors.status.info,
    // RGB versions for opacity support
    '--team-primary-rgb': hexToRgb(colors.primary),
    '--team-secondary-rgb': hexToRgb(colors.secondary),
    '--team-accent-rgb': hexToRgb(colors.accent),
  };
}

/**
 * Team Context
 */
const TeamContext = createContext(null);

/**
 * Team Provider Component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.initialTeamId] - Override initial team ID
 * @param {boolean} [props.enableApiSync] - Enable API sync (default: true)
 */
export function TeamProvider({ children, initialTeamId, enableApiSync = true }) {
  // Use default config for initial render, hydrate from localStorage on client
  const [teamId, setTeamId] = useState(initialTeamId || defaultTeamId);
  const [config, setConfig] = useState(getTeamConfig(initialTeamId || defaultTeamId));
  const [isLoading, setIsLoading] = useState(true); // Start true - wait for hydration
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);

  // Dynamic team registry (merged hardcoded + API)
  const [dynamicRegistry, setDynamicRegistry] = useState(teamRegistry);
  const apiSyncAttempted = useRef(false);

  // Sport selection state - start as false, will be set from localStorage
  const [hasSportSelected, setHasSportSelected] = useState(false);
  const [selectedSport, setSelectedSport] = useState(null);

  // Selected team from NatStat (sport/league/team selection)
  // This is separate from teamId which is the SLAM platform team config
  const [selectedTeam, setSelectedTeamState] = useState(null);

  // Current sport code (derived from config)
  const sportCode = config?.sportCode || null;
  const sportCategory = sportCode ? getSportCategory(sportCode) : null;

  // Hydrate from localStorage on client mount ONLY
  useEffect(() => {
    if (mounted) return; // Only run once

    setMounted(true);

    // Hydrate team config from localStorage (if not overridden by initialTeamId prop)
    let currentConfig = config;
    if (!initialTeamId) {
      const storedId = getInitialTeamId();
      if (storedId !== teamId) {
        setTeamId(storedId);
        currentConfig = getTeamConfig(storedId);
        setConfig(currentConfig);
      }
    }

    // DERIVE sport from the team's sportCode (single source of truth)
    // This ensures sport selection always matches the default team
    if (typeof window !== 'undefined' && currentConfig?.sportCode) {
      const derivedCategory = getSportCategory(currentConfig.sportCode);
      if (derivedCategory) {
        setHasSportSelected(true);
        setSelectedSport(derivedCategory);
        localStorage.setItem(SPORT_STORAGE_KEY, derivedCategory);
        localStorage.setItem(SPORT_SELECTED_FLAG, 'true');
      }
    }

    // Hydrate selected team from localStorage (NatStat team selection)
    if (typeof window !== 'undefined') {
      const storedTeam = localStorage.getItem(SELECTED_TEAM_KEY);
      if (storedTeam) {
        try {
          const parsed = JSON.parse(storedTeam);
          const normalized = normalizeSelectedTeamSelection(parsed, {
            sportCategory: getSportCategory(currentConfig?.sportCode),
            sportCode: currentConfig?.sportCode || null,
            teamName: currentConfig?.name || null,
            teamId: currentConfig?.id || null,
          });
          setSelectedTeamState(normalized);
          localStorage.setItem(SELECTED_TEAM_KEY, JSON.stringify(normalized));
        } catch (e) {
          console.warn('Failed to parse stored selected team:', e);
        }
      }
    }

    // Mark hydration complete
    setIsLoading(false);
  }, [mounted, initialTeamId, teamId, config]);

  // Fetch team configs from API (after hydration)
  useEffect(() => {
    if (!mounted || !enableApiSync || apiSyncAttempted.current) return;

    apiSyncAttempted.current = true;

    fetchTeamConfigsFromAPI().then((mergedRegistry) => {
      setDynamicRegistry(mergedRegistry);

      // If current team has an updated config from API, apply it
      if (mergedRegistry[teamId]) {
        const updatedConfig = mergedRegistry[teamId];
        if (updatedConfig !== config) {
          setConfig(updatedConfig);
        }
      }
    });
  }, [mounted, enableApiSync, teamId, config]);

  // Apply CSS variables to document root
  useEffect(() => {
    if (!mounted) return;

    const cssVars = colorsToCSSVariables(config);
    const root = document.documentElement;

    Object.entries(cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Also set data attribute for CSS selectors
    root.setAttribute('data-team', config.id);
  }, [config, mounted]);

  // Switch team function (uses dynamicRegistry for API-enhanced configs)
  // Also automatically derives and sets the sport based on team's sportCode
  const switchTeam = useCallback(async (newTeamId) => {
    setIsLoading(true);
    setError(null);

    try {
      // Check dynamic registry first, then hardcoded
      const newConfig = dynamicRegistry[newTeamId] || getTeamConfig(newTeamId);

      if (!newConfig) {
        throw new Error(`Team configuration not found: ${newTeamId}`);
      }

      setTeamId(newTeamId);
      setConfig(newConfig);

      // Derive sport category from team's sportCode and update sport selection
      const teamSportCode = newConfig.sportCode;
      const derivedCategory = teamSportCode ? getSportCategory(teamSportCode) : null;
      if (derivedCategory) {
        setSelectedSport(derivedCategory);
        setHasSportSelected(true);
        if (typeof window !== 'undefined') {
          localStorage.setItem(SPORT_STORAGE_KEY, derivedCategory);
          localStorage.setItem(SPORT_SELECTED_FLAG, 'true');
        }
      }

      // Persist team to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, newTeamId);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to switch team'));
    } finally {
      setIsLoading(false);
    }
  }, [dynamicRegistry]);

  // Get CSS variables
  const getCSSVariables = useCallback(() => {
    return colorsToCSSVariables(config);
  }, [config]);

  // Get available teams (from dynamic registry)
  const getAvailableTeams = useCallback(() => {
    return Object.keys(dynamicRegistry);
  }, [dynamicRegistry]);

  // Get teams by sport code
  const getTeamsBySport = useCallback((code) => {
    return Object.entries(dynamicRegistry)
      .filter(([, teamConfig]) => teamConfig.sportCode === code)
      .map(([id]) => id);
  }, [dynamicRegistry]);

  // Get teams by sport category
  const getTeamsByCategory = useCallback((category) => {
    const codes = SPORT_CATEGORY_TO_CODES[category] || [];
    return Object.entries(dynamicRegistry)
      .filter(([, teamConfig]) => codes.includes(teamConfig.sportCode))
      .map(([id]) => id);
  }, [dynamicRegistry]);

  // Select sport for UI filtering purposes (does NOT auto-switch teams)
  // The actual platform sport is derived from the default team's sportCode
  // This function is used by Settings to filter the team list
  const selectSport = useCallback((sport) => {
    setSelectedSport(sport);
    setHasSportSelected(true);

    if (typeof window !== 'undefined') {
      localStorage.setItem(SPORT_STORAGE_KEY, sport);
      localStorage.setItem(SPORT_SELECTED_FLAG, 'true');
    }

    // NOTE: We intentionally do NOT auto-switch teams here anymore.
    // The team selection determines the platform sport, not the other way around.
    // Users must explicitly select a team after choosing their sport.
  }, []);

  // Clear sport selection (for logout/reset)
  const clearSportSelection = useCallback(() => {
    setSelectedSport(null);
    setHasSportSelected(false);

    if (typeof window !== 'undefined') {
      localStorage.removeItem(SPORT_STORAGE_KEY);
      localStorage.removeItem(SPORT_SELECTED_FLAG);
    }
  }, []);

  // Set selected team from NatStat (sport/league/team hierarchy)
  // This stores the user's actual team selection for roster population
  const setSelectedTeam = useCallback((teamSelection) => {
    const normalized = normalizeSelectedTeamSelection(teamSelection, {
      sportCategory: getSportCategory(config?.sportCode),
      sportCode: config?.sportCode || null,
      teamName: config?.name || null,
      teamId: config?.id || null,
    });

    setSelectedTeamState(normalized);

    if (typeof window !== 'undefined') {
      if (normalized) {
        localStorage.setItem(SELECTED_TEAM_KEY, JSON.stringify(normalized));
      } else {
        localStorage.removeItem(SELECTED_TEAM_KEY);
      }
    }

    // Also update sport selection based on the team's sport
    if (normalized?.sport) {
      setSelectedSport(normalized.sport);
      setHasSportSelected(true);
      if (typeof window !== 'undefined') {
        localStorage.setItem(SPORT_STORAGE_KEY, normalized.sport);
        localStorage.setItem(SPORT_SELECTED_FLAG, 'true');
      }
    }
  }, [config]);

  // Clear selected team (for logout/reset)
  const clearSelectedTeam = useCallback(() => {
    setSelectedTeamState(null);

    if (typeof window !== 'undefined') {
      localStorage.removeItem(SELECTED_TEAM_KEY);
    }
  }, []);

  // Memoized context value
  const contextValue = useMemo(() => ({
    // Current team config
    config,
    teamId,
    isLoading,
    error,

    // Sport information (derived from config.sportCode)
    sportCode,
    sportCategory,
    SPORT_CODES,
    SPORT_CATEGORIES,

    // Team switching
    switchTeam,
    getAvailableTeams,
    getTeamsBySport,
    getTeamsByCategory,

    // Sport selection (UI-level category)
    hasSportSelected,
    selectedSport,
    selectSport,
    clearSportSelection,

    // NatStat team selection (sport/league/team hierarchy)
    selectedTeam,
    setSelectedTeam,
    clearSelectedTeam,

    // Utilities
    getCSSVariables,
  }), [
    config, teamId, isLoading, error,
    sportCode, sportCategory,
    switchTeam, getAvailableTeams, getTeamsBySport, getTeamsByCategory,
    hasSportSelected, selectedSport, selectSport, clearSportSelection,
    selectedTeam, setSelectedTeam, clearSelectedTeam,
    getCSSVariables,
  ]);

  return (
    <TeamContext.Provider value={contextValue}>
      {children}
    </TeamContext.Provider>
  );
}

/**
 * Hook to access team configuration
 * @returns {Object} Team context value
 */
export function useTeam() {
  const context = useContext(TeamContext);

  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }

  return context;
}

/**
 * Hook to access just the team config (convenience)
 * @returns {Object} Team configuration
 */
export function useTeamConfig() {
  const { config } = useTeam();
  return config;
}

/**
 * Hook to access team colors
 * @returns {Object} Team colors
 */
export function useTeamColors() {
  const { config } = useTeam();
  return config.colors;
}

/**
 * Hook to access team branding
 * @returns {Object} Team branding
 */
export function useTeamBranding() {
  const { config } = useTeam();
  return config.branding;
}

/**
 * Hook to access team features
 * @returns {Object} Team features
 */
export function useTeamFeatures() {
  const { config } = useTeam();
  return config.features;
}

/**
 * Hook to check if a feature is enabled
 * @param {string} feature - Feature name
 * @returns {boolean} Whether feature is enabled
 */
export function useFeatureEnabled(feature) {
  const features = useTeamFeatures();

  if (feature in features) {
    const value = features[feature];
    return typeof value === 'boolean' ? value : true;
  }

  // Check custom features
  if (features.custom && feature in features.custom) {
    return features.custom[feature];
  }

  return false;
}

/**
 * Hook to access sport information
 * @returns {Object} Sport code and category
 */
export function useSportInfo() {
  const { sportCode, sportCategory, SPORT_CODES, SPORT_CATEGORIES } = useTeam();
  return { sportCode, sportCategory, SPORT_CODES, SPORT_CATEGORIES };
}

/**
 * Hook to access team analysis configuration
 * Used for generating system prompts with team-specific context
 * @returns {Object} Analysis configuration
 */
export function useTeamAnalysis() {
  const { config, sportCode } = useTeam();
  return {
    sportCode,
    systemPromptAdditions: config.analysis?.systemPromptAdditions,
    focusAreas: config.analysis?.focusAreas || [],
    terminology: config.analysis?.terminology || {},
    defaultLanguage: config.analysis?.defaultLanguage || 'en',
  };
}

/**
 * Hook to get team config for API requests
 * Returns a serializable object for sending to backend
 * @returns {Object} Serializable team config for API
 */
export function useTeamConfigForAPI() {
  const { config, sportCode, teamId } = useTeam();
  return {
    teamId,
    sportCode,
    analysis: config.analysis,
    branding: {
      platform: config.analysis?.terminology?.platform || config.branding?.name,
      analyst: config.analysis?.terminology?.analyst,
      report: config.analysis?.terminology?.report,
    },
  };
}

/**
 * Hook to access selected NatStat team
 * Returns the user's team selection (sport/league/team) from the team selector
 * @returns {Object} Selected team data { sport, league, team, selectedAt }
 */
export function useSelectedTeam() {
  const { selectedTeam, setSelectedTeam, clearSelectedTeam } = useTeam();
  return { selectedTeam, setSelectedTeam, clearSelectedTeam };
}

export default TeamProvider;
